/* eslint-disable camelcase */
import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'

import type { FieldValues } from 'react-hook-form'
import { useForm } from 'react-hook-form'
import * as z from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'

import type { FC } from 'react'

import Button from '@segment/matcha/components/Button'

import {
  CompanyEmailField,
  companyEmailMessages,
  FullNameField,
  fullNameMessages,
  SegmentWorkspaceField,
  segmentWorkspaceMessages,
  CompanyJobTitle,
  jobTitleMessages,
} from '@segment/templates/FormFields'

import Disclaimer from '@segment/templates/Disclaimer'

import FormSubmitAction from '@segment/templates/FormSubmitAction'

import TrackableForm, { errorMessage } from '@segment/templates/TrackableForm'

import { trackProgramSignupForm } from '@segment/tracking/scripts'

import useMatchMedia from '@segment/utils/useMatchMedia'
import { scrollToAnchor } from '@segment/utils/useScrollToAnchor'

import sharedEmailRegex from '@segment/utils/regexes/sharedEmail'
import emailRegex from '@segment/utils/regexes/email'
import formRegex from '@segment/utils/regexes/form'
import lastNameRegex from '@segment/utils/regexes/lastName'

import WarehouseField, { warehouseMessages, warehouseCheckboxesMessages } from './WarehouseField'

const formData = {
  id: `linked-profiles-beta-request`,
  buttonText: `Request a Beta Invitation`,
  successMessage: `You’re on the list for the Linked Profiles Beta!`,
  warehouses: [`Snowflake`, `BigQuery`, `Redshift`, `Azure Synapse`, `Databricks`, `Postgres`],
}

const schema = z
  .object({
    name: z
      .string({
        required_error: fullNameMessages.required,
      })
      .regex(formRegex, { message: fullNameMessages.invalid })
      .regex(lastNameRegex, { message: fullNameMessages.invalid }),
    email: z
      .string({
        required_error: companyEmailMessages.required,
      })
      .regex(formRegex, { message: companyEmailMessages.invalid })
      .regex(emailRegex, {
        message: companyEmailMessages.invalid,
      }) /* This is equal to .email( { message: companyEmailMessages.invalid }) but with a custom regex */
      .regex(sharedEmailRegex, {
        message: companyEmailMessages.sharedEmail,
      }),
    jobTitle: z
      .string({
        required_error: jobTitleMessages.required,
      })
      .regex(formRegex, { message: jobTitleMessages.invalid }),
    workspace: z
      .string({
        required_error: segmentWorkspaceMessages.required,
      })
      .regex(formRegex, { message: segmentWorkspaceMessages.invalid }),
    selectedWarehouses: z.string().array().nonempty({
      message: warehouseCheckboxesMessages.required,
    }),
    otherWarehouse: z
      .string({
        required_error: warehouseMessages.required,
      })
      .regex(formRegex, { message: warehouseMessages.invalid }),
  })
  .partial({ otherWarehouse: true })
/* eslint-enable camelcase */

const HeroForm: FC = () => {
  const theme = useTheme()
  const mobileResolutionMediaQuery = theme.breakpoints.down(`md`, false)
  const isSmallResolution = useMatchMedia(mobileResolutionMediaQuery, true)

  const methods = useForm<FieldValues>({
    mode: `onBlur`,
    reValidateMode: `onBlur`,
    shouldUnregister: true,
    resolver: zodResolver(schema),
    defaultValues: {
      selectedWarehouses: [],
    },
  })

  return (
    <FormWrapper id={formData.id}>
      <TrackableForm
        gap="small"
        methods={methods}
        success={
          <FormSubmitAction
            status="success"
            data-hook="linked-profiles-beta-request-success-message"
            heading={formData.successMessage}
          />
        }
        error={
          <FormSubmitAction status="error" data-hook="linked-profiles-beta-request-error-message" {...errorMessage} />
        }
        onSubmit={(data) => {
          scrollToAnchor(formData.id, isSmallResolution ? -150 : -120, false)
          trackProgramSignupForm(data)
        }}
        customFormIdentifier={formData.id}
      >
        <FullNameField label="Full Name" />
        <CompanyEmailField label="Email Address" placeholder="Email of your Segment Workspace" />
        <CompanyJobTitle label="Job Title" name="jobTitle" id="jobTitle" placeholder="Job title" />
        <SegmentWorkspaceField label="Segment Workspace Name" />
        <WarehouseField data={formData.warehouses} />
        <ButtonWrapper>
          <Button color="primary" variant="filled" size="large" expanded={true}>
            {formData.buttonText}
          </Button>
        </ButtonWrapper>
        <DisclaimerWrapper>
          <Disclaimer />
        </DisclaimerWrapper>
      </TrackableForm>
    </FormWrapper>
  )
}

const FormWrapper = styled.div`
  text-align: left;
`

const ButtonWrapper = styled.div``

const DisclaimerWrapper = styled.div``

export default HeroForm
